import React, {
    ForwardedRef,
    forwardRef,
    useCallback,
    useLayoutEffect,
    useImperativeHandle,
    useRef,
    cloneElement,
    ReactElement,
    isValidElement,
} from 'react';
import AutoSizer, { Size } from 'react-virtualized/dist/es/AutoSizer';
import List, { ListProps, ListRowProps, RenderedRows } from 'react-virtualized/dist/es/List';
import WindowScroller from 'react-virtualized/dist/es/WindowScroller';
import { withInvariant } from '../../utils/withInvariant';
import { Box } from './Box';
import { Stack } from './Stack';

interface VirtualizedListRef {
    resize: (size: Size) => void;
}

interface VirtualizedListProps extends Omit<ListProps, 'rowCount'> {
    width: number;
    height: number;
    count: number;
    perPage: number;
    scrollTop: number;
    isLoading: boolean;
    isEndReached: boolean;
    rowHeight: (width: number, index: number) => number;
    estimatedRowSize: (width: number) => number;
    renderItem: (index: number) => ReactElement;
}

export const VirtualizedList = forwardRef(
    (props: VirtualizedListProps, ref: ForwardedRef<VirtualizedListRef | null>) => {
        const { perPage, count, renderItem, estimatedRowSize, isLoading, isEndReached, ...other } =
            props;
        const listRef = useRef<List>(null);
        const rowRenderer = useCallback(
            ({ index, key, style }: ListRowProps) => {
                const element = renderItem(index);

                // @ts-ignore
                return isValidElement(element) ? cloneElement(element, { style, key }) : null;
            },
            [renderItem],
        );

        const rowHeight = useCallback(
            (params: { index: number }) => props.rowHeight(props.width, params.index),
            [props.width, props.rowHeight],
        );

        useImperativeHandle(ref, () => ({
            resize() {
                listRef.current?.recomputeRowHeights(0);
            },
        }));

        // Размеры скелетона и загруженного элемента списка могут различаться по высоте,
        // поэтому нужно сбросить кэш расчетов высоты
        useLayoutEffect(() => {
            const startIndex = Math.max(0, count - perPage - 1);

            if (startIndex >= 0) {
                listRef.current?.recomputeRowHeights(startIndex);
            }
        }, [count, perPage, listRef.current]);

        return (
            <List
                {...other}
                tabIndex={-1}
                ref={listRef}
                autoHeight
                rowCount={!isLoading || isEndReached ? count : count + perPage}
                rowHeight={rowHeight}
                estimatedRowSize={estimatedRowSize(props.width)}
                rowRenderer={rowRenderer}
            />
        );
    },
);

interface InfiniteListProps {
    threshold?: number;
    count: number;
    perPage: number;
    isLoading: boolean;
    isEndReached: boolean;
    onLoadNext?: VoidFunction;
    rowHeight: VirtualizedListProps['rowHeight'];
    renderItem: VirtualizedListProps['renderItem'];
    estimatedRowSize: VirtualizedListProps['estimatedRowSize'];
}

export const InfiniteList = withInvariant(
    (props: InfiniteListProps) => {
        const containerRef = useRef<VirtualizedListRef | null>(null);
        const { isLoading, count, threshold = 10, isEndReached, onLoadNext, ...other } = props;
        const onResize = useCallback(
            (size: Size) => containerRef.current?.resize(size),
            [containerRef.current],
        );

        const onRowsRendered = useCallback(
            (params: RenderedRows) => {
                const isScrollToEnd = params.stopIndex + threshold >= count;

                if (isScrollToEnd && !isLoading && !isEndReached) {
                    onLoadNext?.();
                }
            },
            [onLoadNext, isLoading, isEndReached, threshold, count],
        );

        return (
            <WindowScroller>
                {({ height, scrollTop, registerChild }) => (
                    <Box width="100%" ref={registerChild as any}>
                        <AutoSizer disableHeight onResize={onResize}>
                            {({ width }: Size) => (
                                <VirtualizedList
                                    {...other}
                                    ref={containerRef}
                                    count={count}
                                    width={width}
                                    height={height}
                                    scrollTop={scrollTop}
                                    isLoading={isLoading}
                                    isEndReached={isEndReached}
                                    onRowsRendered={onRowsRendered}
                                />
                            )}
                        </AutoSizer>
                    </Box>
                )}
            </WindowScroller>
        );
    },
    ({ count, renderItem }: InfiniteListProps) => (
        <Stack
            width="100%"
            maxWidth="100%"
            count={count}
            renderItem={(index) => cloneElement(renderItem(index), { key: index })}
        />
    ),
);
